import React from 'react';
import './PoliciesPage.scss';
import company from '../config/companyInfo';

const LOGO_URL = 'https://www.esaienergy.com/hubfs/Imported_Blog_Media/638777a841191e721b31c1f5_ESAI%20Logo_Black-p-500-1.png';
const SUPPORT_EMAIL = 'support@esai.com';
const LAST_UPDATED = 'March 2025';

function PoliciesPage() {
  // App.scss sets body { overflow: hidden } for the dashboard layout.
  // Override it while this standalone page is mounted so it can scroll normally.
  React.useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'auto';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div className="pp-root">
      {/* Header */}
      <header className="pp-header">
        <div className="pp-header-inner">
          <a href="/" className="pp-header-home">
            <img src={LOGO_URL} alt="ESAI Energy" className="pp-header-logo" />
          </a>
          <nav className="pp-header-nav">
            <a
              href="/"
              className="pp-header-link"
              onClick={(e) => { if (window.history.length > 1) { e.preventDefault(); window.history.back(); } }}
            >← Back to ESAI Connect</a>
          </nav>
        </div>
      </header>

      {/* Main */}
      <main className="pp-main">
        <div className="pp-container">
          {/* Page title */}
          <div className="pp-page-title">
            <h1>Legal &amp; Security</h1>
            <p>Policies governing your use of ESAI Connect and how we protect your data.</p>
          </div>

          {/* On-page nav */}
          <nav className="pp-toc">
            <a href="#privacy-policy" className="pp-toc-link">Privacy Policy</a>
            <span className="pp-toc-sep">|</span>
            <a href="#cybersecurity" className="pp-toc-link">Cybersecurity Policy</a>
          </nav>

          {/* ─── Privacy Policy ─────────────────────────────────────────── */}
          <section id="privacy-policy" className="pp-section">
            <h2 className="pp-section-heading">Privacy Policy</h2>
            <p className="pp-meta">ESAI Energy LLC · Last updated: {LAST_UPDATED}</p>

            <p>
              This Privacy Policy describes how ESAI Energy LLC ("ESAI", "we", "our", or "us") collects,
              uses, and protects information obtained through ESAI Connect (<em>connect.esai.com</em>),
              our client data platform. By accessing or using ESAI Connect, you agree to the practices
              described in this policy.
            </p>

            <h3>Information We Collect</h3>
            <p>We collect information in the following ways:</p>
            <ul>
              <li>
                <strong>Account information.</strong> When your organisation is provisioned access to ESAI
                Connect, we associate your Microsoft account identity (name, email address, and
                organisational tenant) with your customer record. This information is used solely to
                authenticate you and to apply the correct data access permissions to your session.
              </li>
              <li>
                <strong>Usage data.</strong> We collect information about how you interact with ESAI
                Connect, including pages visited, features used, and session timing. This data is used
                to improve the platform and diagnose technical issues.
              </li>
              <li>
                <strong>Support communications.</strong> If you contact us at{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>, we retain the content of those
                communications to resolve your inquiry and improve our service.
              </li>
            </ul>

            <h3>How We Use Your Information</h3>
            <p>We use collected information to:</p>
            <ul>
              <li>Authenticate users and enforce customer-specific access permissions</li>
              <li>Deliver the ESAI Connect platform and its data products</li>
              <li>Monitor platform performance, availability, and security</li>
              <li>Respond to support requests and resolve technical issues</li>
              <li>Comply with applicable laws and regulations</li>
            </ul>
            <p>
              We do not sell, rent, or otherwise transfer your personal information to third-party
              advertisers or data brokers.
            </p>

            <h3>Data Protection</h3>
            <p>
              ESAI Connect is hosted on Microsoft Azure infrastructure. All data is encrypted in transit
              using TLS 1.2 or higher and encrypted at rest using Azure-managed encryption keys. Access to
              production systems is restricted to authorised personnel only.
            </p>

            <h3>Cookies and Local Storage</h3>
            <p>
              ESAI Connect uses browser local storage and session cookies to maintain your authenticated
              session (managed by the Microsoft Authentication Library, MSAL) and to cache application
              data for performance. We do not use tracking cookies or third-party advertising cookies.
            </p>

            <h3>Third-Party Services</h3>
            <p>
              Authentication is provided by Microsoft Entra ID. By using ESAI Connect, you interact with
              Microsoft's identity platform, which is governed by Microsoft's own privacy policy. ESAI
              Connect does not share your personal data with any other third parties except as required
              by law.
            </p>

            <h3>Third-Party Links</h3>
            <p>
              ESAI Connect may contain links to external websites, including esaienergy.com. ESAI is not
              responsible for the privacy practices of those sites and encourages you to review their
              policies separately.
            </p>

            <h3>Data Retention</h3>
            <p>
              Account and session data is retained for the duration of your organisation's subscription.
              Upon termination of access, personal account data is removed within 90 days. Aggregated,
              anonymised usage data may be retained indefinitely for platform improvement purposes.
            </p>

            <h3>Your Rights</h3>
            <p>
              Depending on your jurisdiction, you may have rights to access, correct, or request deletion
              of your personal data. To exercise these rights, contact us at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </p>

            <h3>Policy Updates</h3>
            <p>
              We may update this Privacy Policy from time to time. Changes will be posted on this page
              with an updated date. Continued use of ESAI Connect after changes are posted constitutes
              acceptance of the updated policy.
            </p>

            <h3>Contact</h3>
            <p>
              Questions about this Privacy Policy should be directed to{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </p>
          </section>

          {/* ─── Cybersecurity Policy ────────────────────────────────────── */}
          <section id="cybersecurity" className="pp-section">
            <h2 className="pp-section-heading">Cybersecurity Policy</h2>
            <p className="pp-meta">ESAI Energy LLC · Last updated: {LAST_UPDATED}</p>

            <p>
              ESAI Energy LLC is committed to protecting the confidentiality, integrity, and availability
              of ESAI Connect and the data it contains. This policy outlines the security controls and
              responsibilities that govern ESAI Connect.
            </p>

            <h3>Infrastructure Security</h3>
            <p>
              ESAI Connect is hosted entirely on Microsoft Azure, a cloud platform that maintains
              comprehensive security certifications including ISO/IEC 27001, SOC 1 and SOC 2 (Type II),
              and FedRAMP. Azure's physical and network security controls apply to all ESAI Connect
              infrastructure.
            </p>

            <h3>Data Encryption</h3>
            <ul>
              <li>
                <strong>In transit.</strong> All communication between your browser and ESAI Connect
                is encrypted using TLS 1.2 or higher. HTTP connections are automatically redirected to
                HTTPS.
              </li>
              <li>
                <strong>At rest.</strong> All data stored in Azure Cosmos DB is encrypted at rest using
                256-bit AES encryption with Azure-managed keys.
              </li>
            </ul>

            <h3>Authentication and Access Control</h3>
            <p>
              ESAI Connect uses Microsoft Entra ID (formerly Azure Active Directory) for user
              authentication. Key controls include:
            </p>
            <ul>
              <li>Single Sign-On (SSO) via your organisation's Microsoft tenant</li>
              <li>
                Multi-Factor Authentication (MFA) enforcement is governed by your organisation's
                Entra ID policies — ESAI strongly recommends enabling MFA for all accounts with
                access to ESAI Connect
              </li>
              <li>
                Customer-specific data permissions: each account is granted access only to the data
                products, regions, and page sections that your organisation has subscribed to
              </li>
              <li>
                Access tokens expire automatically; sessions are not persistent beyond token lifetime
              </li>
            </ul>

            <h3>Administrative Access</h3>
            <p>
              Access to production infrastructure and databases is restricted to a small number of
              authorised ESAI and Fast Forward technical personnel. Access is granted on a
              least-privilege basis and reviewed periodically. All production access is logged.
            </p>

            <h3>Application Security</h3>
            <p>
              ESAI Connect is developed and maintained following secure development practices, including:
            </p>
            <ul>
              <li>Input validation and output encoding to prevent injection attacks</li>
              <li>Dependency management with regular review of third-party packages</li>
              <li>Code review prior to deployment</li>
              <li>Static web application hosting with no server-side code execution exposed directly to users</li>
            </ul>

            <h3>Monitoring and Logging</h3>
            <p>
              Application and infrastructure logs are collected in Azure Monitor. ESAI monitors for
              unusual access patterns, failed authentication attempts, and infrastructure anomalies.
              Logs are retained for a minimum of 90 days.
            </p>

            <h3>Incident Response</h3>
            <p>
              In the event of a confirmed security incident affecting ESAI Connect, ESAI will:
            </p>
            <ul>
              <li>Contain and remediate the incident as promptly as possible</li>
              <li>Notify affected customers within a reasonable timeframe, as required by applicable law</li>
              <li>Conduct a post-incident review to prevent recurrence</li>
            </ul>

            <h3>User Responsibilities</h3>
            <p>Users of ESAI Connect are expected to:</p>
            <ul>
              <li>Keep their Microsoft account credentials secure and not share them</li>
              <li>
                Enable Multi-Factor Authentication (MFA) on their Microsoft account if not already
                enforced by their organisation
              </li>
              <li>
                Report any suspected security issues, unusual behaviour, or unauthorised access
                immediately to <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              </li>
              <li>Access ESAI Connect only from devices that are reasonably secured and up to date</li>
              <li>
                Not attempt to access data, accounts, or features beyond what has been explicitly
                granted to their account
              </li>
            </ul>

            <h3>Vulnerability Disclosure</h3>
            <p>
              If you discover a potential security vulnerability in ESAI Connect, we ask that you
              disclose it responsibly by emailing{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> with a description of the issue.
              Please do not publicly disclose the vulnerability until we have had a reasonable
              opportunity to investigate and address it. We will acknowledge receipt within two
              business days.
            </p>

            <h3>Policy Updates</h3>
            <p>
              This policy is reviewed and updated periodically to reflect changes to ESAI Connect,
              applicable regulations, and the evolving security landscape. Updates will be posted on
              this page with a revised date.
            </p>

            <h3>Contact</h3>
            <p>
              Security-related enquiries should be directed to{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </p>
          </section>

          {/* Footer */}
          <div className="pp-page-footer">
            <span>{company.legalName}</span>
            <span className="pp-footer-sep">|</span>
            <span>{company.address}</span>
            <span className="pp-footer-sep">|</span>
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </div>
        </div>
      </main>
    </div>
  );
}

export default PoliciesPage;
